import { useEffect, useState } from "react";
import { changeAdminPassword, getAdminUser, updateAdminUser, uploadFile } from "@/api/admin.api.js";
import PhoneFields from "@/components/PhoneFields.jsx";
import RowActionsMenu from "@/components/RowActionsMenu.jsx";
import { productImageUrl } from "@/utils/format.js";
import {
    buildAdminProfilePayload,
    createEmptyAdminProfile,
    normalizeAdminPhones
} from "@/utils/adminProfile.js";

export default function AdminAccountSection({ section, setMessage, setError }) {
    const [profile, setProfile] = useState(createEmptyAdminProfile());
    const [profileEditing, setProfileEditing] = useState(false);
    const [photoUploading, setPhotoUploading] = useState(false);
    const [passwords, setPasswords] = useState({ oldPassword: "", newPassword: "" });
    const [showOldPassword, setShowOldPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [passwordEditing, setPasswordEditing] = useState(false);

    const loadProfile = async () => {
        const user = await getAdminUser();
        setProfile({
            ...createEmptyAdminProfile(),
            ...user,
            phones: normalizeAdminPhones(user.phones)
        });
    };

    useEffect(() => {
        let cancelled = false;

        async function load() {
            setError("");
            try {
                await loadProfile();
            } catch (err) {
                if (!cancelled) {
                    setError(err.message);
                }
            }
        }

        if (section === "profile" || section === "password") {
            load();
        }

        return () => {
            cancelled = true;
        };
    }, [section, setError]);

    const cancelProfileEdit = async () => {
        setProfileEditing(false);
        setError("");
        try {
            await loadProfile();
        } catch (err) {
            setError(err.message);
        }
    };

    const saveProfile = async (event) => {
        event.preventDefault();
        try {
            const updated = await updateAdminUser(buildAdminProfilePayload(profile));
            setProfile({
                ...updated,
                phones: normalizeAdminPhones(updated.phones)
            });
            setProfileEditing(false);
            setMessage("Profil mis à jour");
            setError("");
        } catch (err) {
            setError(err.message);
        }
    };

    const handlePhotoChange = async (event) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }
        setPhotoUploading(true);
        setError("");
        try {
            const response = await uploadFile(file);
            setProfile((current) => ({ ...current, photoId: response.fileId }));
            setMessage("Photo de profil téléversée");
        } catch (err) {
            setError(err.message);
        } finally {
            setPhotoUploading(false);
            event.target.value = "";
        }
    };

    const savePassword = async (event) => {
        event.preventDefault();
        try {
            await changeAdminPassword(passwords);
            setPasswords({ oldPassword: "", newPassword: "" });
            setPasswordEditing(false);
            setMessage("Mot de passe mis à jour");
            setError("");
        } catch (err) {
            setError(err.message);
        }
    };

    const profilePhones = profile.phones?.filter((phone) => phone.phoneNumber?.trim()) || [];

    if (section === "profile") {
        return (
            <section className="card profile-section">
                <div className="section-header">
                    <h2>Mon profil</h2>
                    {!profileEditing && (
                        <RowActionsMenu
                            ariaLabel="Actions du profil"
                            items={[
                                {
                                    id: "edit",
                                    label: "Modifier",
                                    onClick: () => {
                                        setError("");
                                        setProfileEditing(true);
                                    }
                                }
                            ]}
                        />
                    )}
                </div>

                {!profileEditing ? (
                    <>
                        <div className="profile-photo-field">
                            {productImageUrl(profile.photoId) ? (
                                <img
                                    src={productImageUrl(profile.photoId)}
                                    alt=""
                                    className="profile-photo-preview"
                                />
                            ) : (
                                <div className="profile-photo-preview profile-photo-preview--empty">Photo</div>
                            )}
                        </div>

                        <dl className="profile-detail-view">
                            <div className="profile-detail-row">
                                <dt>Nom d'utilisateur</dt>
                                <dd>{profile.username || "—"}</dd>
                            </div>
                            <div className="profile-detail-row">
                                <dt>E-mail</dt>
                                <dd>{profile.email || "—"}</dd>
                            </div>
                            <div className="profile-detail-row">
                                <dt>Prénom</dt>
                                <dd>{profile.firstName || "—"}</dd>
                            </div>
                            <div className="profile-detail-row">
                                <dt>Nom</dt>
                                <dd>{profile.lastName || "—"}</dd>
                            </div>
                            <div className="profile-detail-row">
                                <dt>Numéros de téléphone</dt>
                                <dd>
                                    {profilePhones.length > 0 ? (
                                        <ul className="profile-phone-view-list">
                                            {profilePhones.map((phone, index) => (
                                                <li key={`${phone.phoneNumber}-${index}`}>
                                                    {phone.phoneNumber}
                                                    {phone.label ? ` (${phone.label})` : ""}
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        "—"
                                    )}
                                </dd>
                            </div>
                        </dl>
                    </>
                ) : (
                    <form onSubmit={saveProfile}>
                        <div className="profile-photo-field">
                            {productImageUrl(profile.photoId) ? (
                                <img
                                    src={productImageUrl(profile.photoId)}
                                    alt=""
                                    className="profile-photo-preview"
                                />
                            ) : (
                                <div className="profile-photo-preview profile-photo-preview--empty">Photo</div>
                            )}
                            <label className="btn btn-secondary btn-inline profile-photo-upload">
                                {photoUploading ? "Téléversement..." : "Changer la photo"}
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handlePhotoChange}
                                    disabled={photoUploading}
                                    hidden
                                />
                            </label>
                        </div>

                        <label className="field">
                            Nom d'utilisateur
                            <input value={profile.username || ""} disabled />
                        </label>
                        <label className="field">
                            E-mail (optionnel)
                            <input
                                type="email"
                                value={profile.email || ""}
                                onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                                maxLength={255}
                                autoComplete="email"
                            />
                        </label>
                        <label className="field">
                            Prénom
                            <input
                                value={profile.firstName || ""}
                                onChange={(e) => setProfile({ ...profile, firstName: e.target.value })}
                                required
                            />
                        </label>
                        <label className="field">
                            Nom
                            <input
                                value={profile.lastName || ""}
                                onChange={(e) => setProfile({ ...profile, lastName: e.target.value })}
                                required
                            />
                        </label>

                        <PhoneFields
                            phones={profile.phones}
                            onChange={(phones) => setProfile({ ...profile, phones })}
                            idPrefix="admin-profile-phone"
                            requirePhoneNumber={false}
                        />

                        <div className="profile-form-actions">
                            <button className="btn btn-primary btn-inline" type="submit">Enregistrer le profil</button>
                            <button type="button" className="btn btn-secondary btn-inline" onClick={cancelProfileEdit}>
                                Annuler
                            </button>
                        </div>
                    </form>
                )}
            </section>
        );
    }

    if (section === "password") {
        return (
            <section className="card profile-section">
                <div className="section-header">
                    <h2>Changer le mot de passe</h2>
                    {!passwordEditing && (
                        <RowActionsMenu
                            ariaLabel="Actions du mot de passe"
                            items={[
                                {
                                    id: "edit",
                                    label: "Modifier",
                                    onClick: () => {
                                        setError("");
                                        setPasswordEditing(true);
                                    }
                                }
                            ]}
                        />
                    )}
                </div>

                {!passwordEditing ? (
                    <p className="muted">Votre mot de passe est masqué pour des raisons de sécurité.</p>
                ) : (
                    <form onSubmit={savePassword}>
                        <label className="field">
                            Mot de passe actuel
                            <div className="password-field">
                                <input
                                    type={showOldPassword ? "text" : "password"}
                                    value={passwords.oldPassword}
                                    onChange={(e) => setPasswords({ ...passwords, oldPassword: e.target.value })}
                                    required
                                />
                                <button type="button" className="password-toggle" onClick={() => setShowOldPassword(!showOldPassword)}>
                                    {showOldPassword ? "Masquer" : "Afficher"}
                                </button>
                            </div>
                        </label>
                        <label className="field">
                            Nouveau mot de passe
                            <div className="password-field">
                                <input
                                    type={showNewPassword ? "text" : "password"}
                                    value={passwords.newPassword}
                                    onChange={(e) => setPasswords({ ...passwords, newPassword: e.target.value })}
                                    required
                                />
                                <button type="button" className="password-toggle" onClick={() => setShowNewPassword(!showNewPassword)}>
                                    {showNewPassword ? "Masquer" : "Afficher"}
                                </button>
                            </div>
                        </label>
                        <div className="profile-form-actions">
                            <button className="btn btn-primary btn-inline" type="submit">Enregistrer</button>
                            <button
                                type="button"
                                className="btn btn-secondary btn-inline"
                                onClick={() => {
                                    setPasswordEditing(false);
                                    setPasswords({ oldPassword: "", newPassword: "" });
                                    setError("");
                                }}
                            >
                                Annuler
                            </button>
                        </div>
                    </form>
                )}
            </section>
        );
    }

    return null;
}
